/**
 * 2026-11-03 UAT A15（和田様）。削除の確認が「ドキッとする」ので、削除の前にその場で記録を保存（CSV）できるようにする。
 * 車両・ドライバー・アルコールチェッカー端末・クライアントの削除の確認で、同じ文と同じボタンを出す。
 *
 * 記録はサーバーから全件をページごとに読み（A28 の fetchAllPages。1ページ100件、上限 MAX_EXPORT_ROWS 件）、
 * 一覧の CSV と同じ列で保存する。
 * - 車両: その車両の点検の記録（点検履歴の CSV と同じ列）
 * - ドライバー: その運転者のアルコール検査の記録（アルコール検査の CSV と同じ列。法令で保存する記録）
 * - クライアント: そのクライアントの作業の記録
 * - 端末: 記録は端末に結びつかないため、端末の情報（一覧の CSV と同じ列）の1行
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { csvDateTime, deviceTimeZoneExportFields } from 'config/csv';
import { ExportField, exportRowsToCsv } from 'reusable/export';
import { fetchAllPages } from 'reusable/fetchAllPages';

/** path（`/v1/alcohol_tests/?user=1` など）の記録を、全ページ読んで返す。 */
export function fetchAllRecords(path: string): Promise<any[]> {
  const joiner = path.includes('?') ? (path.endsWith('?') || path.endsWith('&') ? '' : '&') : '?';
  return fetchAllPages((page, pageSize) => `${path}${joiner}page=${page}&page_size=${pageSize}`);
}

/** アルコール検査の CSV と同じ列（components/Alcohol/Toolbar.tsx）。 */
const ALCOHOL_FIELDS: ExportField[] = [
  { header: 'Test ID', field: 'id' },
  { header: 'Driver ID', field: 'user.id' },
  { header: 'Driver Name', field: 'user.name' },
  { header: 'Driver Contact', field: 'user.phone' },
  { header: 'Driver Erased', field: 'user.is_erased', customExport: (erased: unknown) => (erased ? 'Erased' : '') },
  { header: 'Test Results mg/l', field: 'data' },
  { header: 'Date Time', field: 'testing_time', customExport: csvDateTime },
  ...deviceTimeZoneExportFields(),
];

/** 点検履歴の CSV と同じ列（components/Vehicle/Toolbar/inspectionToolbar.tsx）。 */
const INSPECTION_FIELDS: ExportField[] = [
  { header: 'Record ID', field: 'id' },
  { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
  { header: 'Plate Number', field: 'vehicle.plate_number' },
  { header: 'Driver Name', field: 'user.name' },
  { header: 'Maintenance Manager', field: 'maintenance_manager' },
  { header: 'Operations Manager', field: 'operations_manager' },
  ...deviceTimeZoneExportFields(),
];

/** 作業の記録の列（開始・終了と、その時点の端末の時間帯）。 */
const WORK_FIELDS: ExportField[] = [
  { header: 'Record ID', field: 'id' },
  { header: 'Client Name', field: 'client_delivery_info.name' },
  { header: 'Task', field: 'task_info.name' },
  { header: 'Driver Name', field: 'user.name' },
  { header: 'Start Time', field: 'start_time', customExport: csvDateTime },
  { header: 'Stop Time', field: 'stop_time', customExport: csvDateTime },
  ...deviceTimeZoneExportFields(),
  ...deviceTimeZoneExportFields('stop_', 'Stop '),
];

/** 端末の一覧の CSV と同じ列（components/Device/DeviceManagement.tsx）。 */
const DEVICE_FIELDS: ExportField[] = [
  { header: 'Device ID', field: 'device_id' },
  { header: 'Device Name', field: 'name' },
  { header: 'Device Status', field: 'status' },
  { header: 'Assigned User Name', field: 'user_info.name' },
  { header: 'Assigned User Contact', field: 'user_info.email' },
  { header: 'Assigned Date', field: 'assigned_date' },
  { header: 'Start Date', field: 'purchase_date' },
  { header: 'Warranty Date', field: 'warranty_date' },
];

/** 保存した行の数を返す。 */
export type RecordsExport = () => Promise<number>;

const saveAll = async (path: string, fields: ExportField[], name: string, id: string | number) => {
  const rows = await fetchAllRecords(path);
  exportRowsToCsv(rows, fields, name, [['id', id], ['before-delete', 'all']]);
  return rows.length;
};

export const vehicleRecordsExport = (vehicleId: string | number): RecordsExport => () =>
  saveAll(`/v1/vehicle_inspection_records/?vehicle=${vehicleId}`, INSPECTION_FIELDS, 'vehicle-inspections', vehicleId);

export const driverRecordsExport = (driverId: string | number): RecordsExport => () =>
  saveAll(`/v1/alcohol_tests/?user=${driverId}`, ALCOHOL_FIELDS, 'driver-alcohol-tests', driverId);

export const clientRecordsExport = (clientId: string | number): RecordsExport => () =>
  saveAll(`/v1/task_sessions/?client_delivery=${clientId}`, WORK_FIELDS, 'client-work-records', clientId);

export const deviceRecordExport = (device: any): RecordsExport => async () => {
  exportRowsToCsv([device], DEVICE_FIELDS, 'device', [['id', device?.device_id ?? device?.id]]);
  return 1;
};

/**
 * 削除の確認の中に出す「記録を保存（CSV）」。保存中・保存した件数・失敗を、その場で知らせる（role=status）。
 * 削除のボタンは止めない（保存は勧めるだけ）。
 */
export const DeleteExportNotice: React.FC<{ onExport: RecordsExport }> = ({ onExport }) => {
  const { t } = useTranslation('common');
  const [state, setState] = useState<{ kind: 'idle' | 'busy' | 'done' | 'failed'; count?: number }>({ kind: 'idle' });

  const run = async () => {
    if (state.kind === 'busy') return;
    setState({ kind: 'busy' });
    try {
      const count = await onExport();
      setState({ kind: 'done', count });
    } catch (error) {
      console.error('Failed to export records before delete', error);
      setState({ kind: 'failed' });
    }
  };

  const message =
    state.kind === 'busy'
      ? t('delete-export.saving')
      : state.kind === 'done'
      ? state.count === 0
        ? t('delete-export.empty')
        : t('delete-export.saved', { count: state.count })
      : state.kind === 'failed'
      ? t('delete-export.failed')
      : '';

  return (
    <div className="flex flex-col gap-2 rounded-md bg-primary/50 p-3" data-delete-export>
      <p className="text-body-2 text-neutral/300">{t('delete-export.lead')}</p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={run}
          disabled={state.kind === 'busy'}
          className="inline-flex h-10 items-center rounded-md border border-primary/300 bg-white px-4 text-subtitle-2 text-primary/300 disabled:opacity-60"
        >
          {t('delete-export.button')}
        </button>
        <p role="status" className={`text-caption ${state.kind === 'failed' ? 'text-error' : 'text-neutral/300'}`}>
          {message}
        </p>
      </div>
    </div>
  );
};
